import { useEffect, useRef } from 'react';
import gsap from 'gsap/all';
import Flip from 'gsap/Flip';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Container, Text, useUi } from '@weareconceptstudio/core';

//* Components
import Editor from '@/components/Editor';
import TitleAnim from '@/modules/_animation/Title';
import StyledQuotedText from '@/components/StyledQuotedText';

//* Styles
import BenefitsStyle from './style';

//* Utils
import { olive1, olive2, olive3, olive4, olive5 } from './utils';
import { getCssVariablePxValue } from '@/modules/_animation/helpers';

gsap.registerPlugin(ScrollTrigger, Flip);

const Benefits = ({ title, items }) => {
	//! Ref
	const topBlockRef = useRef();
	const bottomBlockRef = useRef();
	const triggerRef = useRef();
	const styleRef = useRef();
	const reverseTrigger = useRef();
	const emptySizeRef = useRef();

	const { winWidth } = useUi();

	useEffect(() => {
		if (topBlockRef.current && bottomBlockRef.current && styleRef.current && emptySizeRef.current) {
			const childrenTop = Array.from(topBlockRef.current.children).map((item) => item.children[0]);
			const bottomChildren = Array.from(bottomBlockRef.current.children).map((item) => item.children[0]);
			const allChildren = [...childrenTop, ...bottomChildren];

			topBlockRef.current.style.height = topBlockRef.current.offsetHeight + 'px';
			bottomBlockRef.current.style.height = bottomBlockRef.current.offsetHeight + 'px';

			const sizeCentredRect = emptySizeRef.current.getBoundingClientRect();

			allChildren.forEach((item) => {
				gsap.set(item, { x: 0, y: 0 });
				const itemRect = item.getBoundingClientRect();

				const dx = sizeCentredRect.left - itemRect.left;
				const dy = sizeCentredRect.top - itemRect.top;
				gsap.set(item, { x: dx, y: dy });
			});

			triggerRef.current = ScrollTrigger.create({
				start: 'top 80%',
				end: 'top top',
				trigger: styleRef.current,
				// markers: true,

				onEnter: () => {
					if (styleRef.current) {
						const size1X = getCssVariablePxValue(styleRef.current, '--topOliveLeftDistance');
						const size1Y = 0;

						const size2X = getCssVariablePxValue(styleRef.current, '--topOliveLeftDistance');
						const size2Y = getCssVariablePxValue(styleRef.current, '--olive2topDistance');

						const size3X = getCssVariablePxValue(styleRef.current, '--olive3rightDistance');
						const size3Y = getCssVariablePxValue(styleRef.current, '--olive3topDistance');

						const size4X = getCssVariablePxValue(styleRef.current, '--olive4rightDistance');
						const size4Y = getCssVariablePxValue(styleRef.current, '--olive4topDistance');

						const size5X = getCssVariablePxValue(styleRef.current, '--olive5rightDistance');
						const size5Y = getCssVariablePxValue(styleRef.current, '--olive5topDistance');

						const positions = [
							[size1X, size1Y],
							[size2X, size2Y],
							[size3X, size3Y],
							[size4X, size4Y],
							[size5X, size5Y],
						];

						const sizeCentredRect = emptySizeRef.current.getBoundingClientRect();

						allChildren.forEach((item, i) => {
							gsap.set(item, { x: 0, y: 0 });
							const [x, y] = positions[i] ?? [0, 0];
							const itemRect = item.getBoundingClientRect();

							const dx = sizeCentredRect.left - itemRect.left;
							const dy = sizeCentredRect.top - itemRect.top;

							gsap.fromTo(item, { x: dx, y: dy }, { x, y, duration: 1, ease: 'power2.out', delay: `${0.03 * i}` });
						});
					}
				},
			});

			reverseTrigger.current = ScrollTrigger.create({
				start: 'top bottom',
				end: 'top top',
				trigger: styleRef.current,
				// markers: true,
				onLeaveBack: () => {
					const sizeCentredRect = emptySizeRef.current.getBoundingClientRect();

					allChildren.forEach((item) => {
						gsap.set(item, { x: 0, y: 0 });
						const itemRect = item.getBoundingClientRect();

						const dx = sizeCentredRect.left - itemRect.left;
						const dy = sizeCentredRect.top - itemRect.top;
						gsap.set(item, { x: dx, y: dy });
					});
				},
			});
		}

		return () => {
			if (topBlockRef.current && bottomBlockRef) {
				topBlockRef.current.style.height = 'auto';
				bottomBlockRef.current.style.height = 'auto';

				const childrenTop = Array.from(topBlockRef.current.children).map((item) => item?.children[0]);
				const bottomChildren = Array.from(bottomBlockRef.current.children).map((item) => item?.children[0]);
				const allChildren = [...childrenTop, ...bottomChildren];

				allChildren.forEach((item) => {
					gsap.set(item, {
						clearProps: 'all',
					});
				});
			}

			if (reverseTrigger.current) {
				reverseTrigger.current.kill();
			}
			if (triggerRef.current) {
				triggerRef.current.kill();
			}
		};
	}, [winWidth]);

	return items?.length > 0 ? (
		<BenefitsStyle
			ref={styleRef}
			className='benefits-wrapper'>
			<Container className='benefits-inner-wrapper'>
				<div className='title-block'>
					<TitleAnim load={true}>
						<StyledQuotedText text={title} />
					</TitleAnim>
				</div>

				<div className='benefits-items-wrapper'>
					<div
						ref={emptySizeRef}
						className='empty-div'
					/>
					<div
						ref={topBlockRef}
						className='top-block'>
						{items.slice(0, 2).map((item, index) => {
							return (
								<div
									key={index}
									className={`item-block olive${index + 1}-block`}>
									<div className={`item-inner-block olive${index + 1}`}>
										{index === 0 ? olive1 : olive2}

										<div className='item-content-block'>
											<Text className='h5 font-larken-light green-1000 align-center item-title'>{item.title}</Text>

											<Editor data={item.description} />
										</div>
									</div>
								</div>
							);
						})}
					</div>

					<div
						ref={bottomBlockRef}
						className='bottom-block'>
						{items.slice(2, 5).map((item, index) => {
							return (
								<div
									key={index}
									className={`item-block olive${index + 3}-block`}>
									<div className={`item-inner-block olive${index + 3}`}>
										{index === 0 ? olive3 : index === 1 ? olive4 : olive5}

										<div className='item-content-block'>
											<Text className='h5 font-larken-light green-1000 align-center item-title'>{item.title}</Text>

											<Editor data={item.description} />
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</div>
			</Container>
		</BenefitsStyle>
	) : null;
};

export default Benefits;
